// Device-only personalization store. Everything lives under one localStorage
// key in this browser; nothing is ever sent anywhere. Spec and policy:
// docs/features/personalization-build-prompt-and-policy.md
//
// Every storage access is guarded: during SSR there is no window, and private
// browsing may throw on read or write. A failed write returns false and the
// caller treats personalization as off — it is never an error shown to a child.

export const STORAGE_KEY = 'tos.personal.v1'
export const VERSION = 1
export const MAX_BYTES = 50 * 1024
// Same bands as the site's "Ages 4–6" / "Ages 6–9" / "Ages 10–13" themes.
export const AGE_BANDS = ['4-6', '6-9', '10-13']
// Bands offered before October 2026, mapped to the nearest current one.
const LEGACY_AGE_BANDS = { '3-5': '4-6', '5-7': '6-9', '8-10': '6-9' }
export const LANGS = ['hi', 'en', 'both']
const MAX_NAME_LENGTH = 40

export const emptyData = () => ({
  v: VERSION,
  activeChildId: null,
  children: [],
  progress: {},
  completed: {},
  favourites: {},
})

const defaultStorage = () => {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch (e) {
    return null
  }
}

const isObject = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
const str = value => (typeof value === 'string' ? value : '')
const num = value =>
  typeof value === 'number' && isFinite(value) && value >= 0 ? value : 0
const slugList = value =>
  Array.isArray(value)
    ? value.filter((s, i) => typeof s === 'string' && s && value.indexOf(s) === i)
    : []

// Bring an older stored shape up to the current VERSION. Return null for a
// shape this code cannot read (e.g. written by a newer release); load() then
// starts empty rather than guessing.
export const migrate = raw => {
  if (!isObject(raw)) return null
  switch (raw.v) {
    case 1:
      return raw
    default:
      return null
  }
}

const cleanAgeBand = band => {
  if (AGE_BANDS.indexOf(band) !== -1) return band
  return LEGACY_AGE_BANDS[band] || null
}

const cleanChild = child => {
  if (!isObject(child)) return null
  const id = str(child.id)
  const name = str(child.name).trim().slice(0, MAX_NAME_LENGTH)
  if (!id) return null
  return {
    id,
    name,
    ageBand: cleanAgeBand(child.ageBand),
    lang: LANGS.indexOf(child.lang) !== -1 ? child.lang : 'en',
  }
}

const cleanProgressEntry = entry => {
  if (!isObject(entry) || !str(entry.storySlug)) return null
  return {
    storySlug: entry.storySlug,
    paraIndex: Math.floor(num(entry.paraIndex)),
    audioSec: num(entry.audioSec),
    lang: str(entry.lang) || null,
    updatedAt: num(entry.updatedAt),
  }
}

// Whitelist the stored fields: anything not in the documented shape is dropped,
// and per-child maps only keep entries for children that still exist.
const sanitize = raw => {
  const data = emptyData()
  if (!isObject(raw)) return data

  const seen = {}
  ;(Array.isArray(raw.children) ? raw.children : []).forEach(c => {
    const child = cleanChild(c)
    if (child && !seen[child.id]) {
      seen[child.id] = true
      data.children.push(child)
    }
  })

  data.activeChildId = seen[raw.activeChildId] ? raw.activeChildId : null

  data.children.forEach(({ id }) => {
    const books = isObject(raw.progress) && isObject(raw.progress[id])
      ? raw.progress[id]
      : {}
    const progress = {}
    Object.keys(books).forEach(book => {
      const entry = cleanProgressEntry(books[book])
      if (entry) progress[book] = entry
    })
    if (Object.keys(progress).length) data.progress[id] = progress

    const completed = slugList(isObject(raw.completed) && raw.completed[id])
    if (completed.length) data.completed[id] = completed

    const favourites = slugList(isObject(raw.favourites) && raw.favourites[id])
    if (favourites.length) data.favourites[id] = favourites
  })

  return data
}

const byteSize = text =>
  typeof TextEncoder !== 'undefined'
    ? new TextEncoder().encode(text).length
    : text.length * 3

// Keep the serialized object under MAX_BYTES. Oldest completed slugs go first
// (taken from whichever child has the longest list); if that is not enough,
// the least recently updated progress entries go next.
const trim = data => {
  let text = JSON.stringify(data)
  while (byteSize(text) > MAX_BYTES) {
    const longest = Object.keys(data.completed).sort(
      (a, b) => data.completed[b].length - data.completed[a].length
    )[0]
    if (longest) {
      data.completed[longest].shift()
      if (!data.completed[longest].length) delete data.completed[longest]
    } else {
      let oldest = null
      Object.keys(data.progress).forEach(child => {
        Object.keys(data.progress[child]).forEach(book => {
          const at = data.progress[child][book].updatedAt
          if (!oldest || at < oldest.at) oldest = { child, book, at }
        })
      })
      if (!oldest) break
      delete data.progress[oldest.child][oldest.book]
      if (!Object.keys(data.progress[oldest.child]).length) {
        delete data.progress[oldest.child]
      }
    }
    text = JSON.stringify(data)
  }
  return text
}

// Read the stored object. Always returns a valid shape: empty when nothing is
// stored, storage is unavailable, or the stored value is unreadable.
export const load = (storage = defaultStorage()) => {
  if (!storage) return emptyData()
  try {
    const text = storage.getItem(STORAGE_KEY)
    if (!text) return emptyData()
    return sanitize(migrate(JSON.parse(text)))
  } catch (e) {
    return emptyData()
  }
}

// Merge `partial` over the stored object (top-level keys replace) and write it.
// Returns the saved data, or null when storage is unavailable or the write
// failed — the caller should then treat personalization as off.
export const save = (partial, storage = defaultStorage()) => {
  if (!storage) return null
  try {
    const data = sanitize({ ...load(storage), ...partial, v: VERSION })
    storage.setItem(STORAGE_KEY, trim(data))
    return data
  } catch (e) {
    return null
  }
}

// Remove everything this feature stored on the device. Returns true on success.
export const reset = (storage = defaultStorage()) => {
  if (!storage) return false
  try {
    storage.removeItem(STORAGE_KEY)
    return true
  } catch (e) {
    return false
  }
}
